import { RESULT_RANGE } from "@loot-os/api-contract";

/**
 * The box editor's simulations: what a shopper's average win per open looks like over 10, 100,
 * 1,000 and 10,000 opens of the draft table, the figures boxed's Retool editor showed as
 * "Sim 10 … Sim 10000". Each size is run many times, so the editor shows the spread (the 5th
 * percentile, the median, the 95th) rather than one run that happened to land high or low.
 *
 * It rolls with its own seeded generator, not the provably-fair roll: this is a forecast of the
 * table, not a replay of anybody's open.
 */

/** The run sizes, as boxed's editor had them. */
export const SIM_OPEN_COUNTS = [10, 100, 1_000, 10_000] as const;
/** Runs per size: enough for stable percentiles, few enough to redo on every edit. */
export const SIM_TRIALS = 200;

export interface SimResult {
  opens: number;
  /** Average value won per open, in cents, at the 5th percentile of runs. */
  p5: number;
  median: number;
  p95: number;
}

/** mulberry32: a small, fast, seedable generator, so a test can pin every roll. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/** The value at fraction `q` of an ascending list, by nearest rank. */
function quantile(sorted: number[], q: number): number {
  const index = Math.min(sorted.length - 1, Math.max(0, Math.round(q * (sorted.length - 1))));
  return sorted[index] ?? 0;
}

/**
 * Simulates the table. Rows with no weight never roll; a table whose weights do not reach
 * `RESULT_RANGE` rolls over the weight it has, so a half-built table still reads sensibly.
 * Returns no results for a table with no weight at all.
 */
export function simulateDropTable(
  items: readonly { weight: number; value: number }[],
  seed: number,
  trials = SIM_TRIALS,
): SimResult[] {
  const rolled = items.filter((item) => item.weight > 0);
  const cumulative: number[] = [];
  let total = 0;
  for (const item of rolled) {
    total += item.weight;
    cumulative.push(total);
  }
  if (total === 0) return [];
  const values = rolled.map((item) => item.value);
  const random = seededRandom(seed);

  const roll = (): number => {
    const r = random() * total;
    let lo = 0;
    let hi = cumulative.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((cumulative[mid] ?? 0) > r) hi = mid;
      else lo = mid + 1;
    }
    return values[lo] ?? 0;
  };

  return SIM_OPEN_COUNTS.map((opens) => {
    const averages: number[] = [];
    for (let trial = 0; trial < trials; trial++) {
      let sum = 0;
      for (let i = 0; i < opens; i++) sum += roll();
      averages.push(sum / opens);
    }
    averages.sort((a, b) => a - b);
    return {
      opens,
      p5: quantile(averages, 0.05),
      median: quantile(averages, 0.5),
      p95: quantile(averages, 0.95),
    };
  });
}

/** Whether the weights are a whole table: the simulations are a forecast only then. */
export function isWholeTable(items: readonly { weight: number }[]): boolean {
  return items.reduce((sum, item) => sum + item.weight, 0) === RESULT_RANGE;
}
